// Boot profiler.
// ===========================================================================
// Why this exists: 3CHA was reported slow to start and the cause was guessed at
// three times before it was measured. The database turned out to be ~250 ms of
// the whole boot, so the time is somewhere between the network, the bundle and
// the reads — and none of that is visible from a laptop on a fast connection.
//
// This records a handful of marks and prints ONE summary line, so the next
// "still slow" comes with numbers attached instead of a hypothesis. It is
// deliberately tiny: no timers, no polling, no network of its own.
//
// Read it three ways:
//   • the console, always, prefixed [3c-boot]
//   • window.__3cBoot — the raw object
//   • add ?boot=1 to the URL for an on-screen overlay (nothing to install, and
//     the only way to see this on the phone it is actually slow on)
// ===========================================================================

const marks = [];
let summarised = false;

// performance.now() is measured from navigation start, so every mark is
// already "milliseconds since the app started loading" with no bookkeeping.
const now = () => {
  try { return Math.round(performance.now()); } catch (_) { return 0; }
};

export function bootMark(name) {
  try {
    if (marks.some((m) => m.name === name)) return; // first occurrence wins
    marks.push({ name, at: now() });
    if (typeof window !== 'undefined') window.__3cBootMarks = marks;
  } catch (_) { /* profiling must never break a boot */ }
}

// Navigation + resource timing: separates "the network was slow" from "the app
// was slow", which is the fork this whole investigation kept guessing at.
function networkFacts() {
  const out = {};
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav) {
      out.ttfb = Math.round(nav.responseStart);
      out.htmlDone = Math.round(nav.responseEnd);
      out.domReady = Math.round(nav.domContentLoadedEventEnd);
      out.fromCache = nav.transferSize === 0;
    }
    const js = performance.getEntriesByType('resource')
      .filter((r) => r.initiatorType === 'script' || /\.js(\?|$)/.test(r.name));
    if (js.length) {
      out.jsFiles = js.length;
      out.jsBytes = Math.round(js.reduce((n, r) => n + (r.transferSize || 0), 0) / 1024);
      out.jsDone = Math.round(Math.max(...js.map((r) => r.responseEnd)));
    }
    // Every Supabase call the boot made, and how long the slowest one took.
    const api = performance.getEntriesByType('resource')
      .filter((r) => /supabase\.co|\/api\//.test(r.name));
    if (api.length) {
      out.apiCalls = api.length;
      out.apiSlowest = Math.round(Math.max(...api.map((r) => r.duration)));
      out.apiTotal = Math.round(api.reduce((n, r) => n + r.duration, 0));
    }
    const c = navigator.connection;
    if (c) { out.net = c.effectiveType; if (c.rtt) out.rtt = c.rtt; }
    if (navigator.hardwareConcurrency) out.cores = navigator.hardwareConcurrency;
  } catch (_) { /* any of these can be absent; none is essential */ }
  return out;
}

export function bootSummary() {
  const net = networkFacts();
  const rel = [];
  let prev = 0;
  marks.forEach((m) => { rel.push(`${m.name} ${m.at}ms (+${m.at - prev})`); prev = m.at; });
  return { total: prev, marks: marks.slice(), timeline: rel, ...net };
}

// Called once the shell has its first screen of real data.
export function bootDone() {
  if (summarised) return;
  summarised = true;
  bootMark('ready');
  try {
    const s = bootSummary();
    if (typeof window !== 'undefined') window.__3cBoot = s;
    // One line, so it survives a screenshot of a phone console.
    const head = `[3c-boot] ready in ${s.total}ms` +
      (s.ttfb != null ? ` · html ${s.ttfb}ms` : '') +
      (s.jsDone != null ? ` · js ${s.jsDone}ms/${s.jsBytes}KB` : '') +
      (s.apiCalls != null ? ` · ${s.apiCalls} api calls, slowest ${s.apiSlowest}ms` : '') +
      (s.net ? ` · ${s.net}${s.rtt ? ' rtt' + s.rtt : ''}` : '');
    console.log(head);
    console.log('[3c-boot] ' + s.timeline.join(' → '));
    try { localStorage.setItem('3c_last_boot', JSON.stringify(s)); } catch (_) {}
    if (/[?&]boot=1/.test(window.location.search)) showOverlay(s, head);
  } catch (_) { /* never break the app to report on it */ }
}

function showOverlay(s, head) {
  try {
    const d = document.createElement('div');
    d.setAttribute('role', 'status');
    d.style.cssText = 'position:fixed;left:8px;right:8px;bottom:8px;z-index:99999;' +
      'background:#0b0b0c;color:#e5e7eb;border:1px solid #2563eb;border-radius:10px;' +
      'padding:10px 12px;font:12px/1.45 ui-monospace,monospace;white-space:pre-wrap;' +
      'box-shadow:0 6px 24px rgba(0,0,0,.5);max-height:45vh;overflow:auto';
    d.textContent = head.replace('[3c-boot] ', '') + '\n\n' + s.timeline.join('\n');
    const x = document.createElement('button');
    x.textContent = 'close';
    x.style.cssText = 'margin-top:8px;padding:4px 10px;font:inherit;background:#2563eb;' +
      'color:#fff;border:none;border-radius:6px';
    x.onclick = () => d.remove();
    d.appendChild(document.createElement('br'));
    d.appendChild(x);
    document.body.appendChild(d);
  } catch (_) {}
}
